import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import api from '../../../services/api';
import CaseVisibilityPanel from '../CaseVisibilityPanel';
import { getMediaUrl } from '../../../utils/mediaUrl';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  publicBodyFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../caseManagerStyles';

const LearningStep = ({ data, onUpdate, visibility, onVisibilityChange, groups, provenance, showVisibilityControls = false }) => {
  const theme = useTheme();
  const learning = data.learning || { summary: '', links: [], attachments: [] };
  const [newLink, setNewLink] = useState({ label: '', url: '' });
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const updateLearning = (nextLearning) => {
    onUpdate({
      learning: {
        summary: nextLearning.summary ?? learning.summary,
        links: nextLearning.links ?? learning.links,
        attachments: nextLearning.attachments ?? learning.attachments
      }
    });
  };

  const handleAddLink = () => {
    const label = newLink.label.trim();
    const url = newLink.url.trim();
    if (!label || !url) {
      return;
    }
    const linkEntry = { id: uuidv4(), label, url };
    updateLearning({ links: [...learning.links, linkEntry] });
    setNewLink({ label: '', url: '' });
  };

  const handleRemoveLink = (id) => {
    updateLearning({ links: learning.links.filter((link) => link.id !== id) });
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['application/pdf'];
    const maxSize = 50 * 1024 * 1024;

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Only PDF files are supported for learning resources.');
        event.target.value = '';
        return;
      }
      if (file.size > maxSize) {
        setUploadError('File size must be under 50MB.');
        event.target.value = '';
        return;
      }
    }

    try {
      setUploading(true);
      setUploadError('');
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const caseId = data.id || 'temp';
      const response = await api.post(`/cases/${caseId}/learning/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploaded = (response.data.files || []).map((file) => ({
        ...file,
        id: uuidv4()
      }));

      updateLearning({ attachments: [...learning.attachments, ...uploaded] });
    } catch (error) {
      console.error('Error uploading learning resources:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload file(s).');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveAttachment = (id) => {
    updateLearning({
      attachments: learning.attachments.filter((file) => (file.id || file.filename) !== id)
    });
  };

  const attachmentUrl = (file) => file.url || getMediaUrl(file.path);

  return (
    <CaseEditorStepFrame>
      <Grid container spacing={2.2}>
        {/* Left Column: Learning Content */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            {/* Summary Card */}
            <Card elevation={0} sx={{ ...caseManagerInsetSurfaceSx, borderRadius: '22px' }}>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <SchoolIcon sx={{ mr: 1, color: theme.palette.primary.main }} /> Key Learning Points
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  label="Learning Summary"
                  value={learning.summary || ''}
                  onChange={(e) => updateLearning({ summary: e.target.value })}
                  placeholder="Summarise the key clinical takeaways, differential diagnoses, and management steps..."
                  helperText="This summary appears to students after they complete the case."
                  sx={publicTextFieldSx}
                />
              </CardContent>
            </Card>

            {/* Resources Card */}
            <Card elevation={0} sx={{ ...caseManagerInsetSurfaceSx, borderRadius: '22px' }}>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <LinkIcon sx={{ mr: 1, color: theme.palette.secondary.main }} /> External Resources
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Add New Link</Typography>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link Title"
                        value={newLink.label}
                        onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                        placeholder="e.g. NICE Guidelines"
                        sx={publicTextFieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="URL"
                        value={newLink.url}
                        onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                        placeholder="https://..."
                        sx={publicTextFieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        onClick={handleAddLink}
                        disabled={!newLink.label || !newLink.url}
                        sx={{ ...publicPrimaryButtonSx, minHeight: 40, height: 40 }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {learning.links.length > 0 ? (
                  <List dense sx={{ bgcolor: 'white', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                    {learning.links.map((link, index) => (
                      <React.Fragment key={link.id}>
                        {index > 0 && <Divider component="li" />}
                        <ListItem
                          secondaryAction={
                            <IconButton edge="end" size="small" onClick={() => handleRemoveLink(link.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={link.label}
                            secondary={link.url}
                            primaryTypographyProps={{ fontWeight: 500, color: theme.palette.primary.main }}
                            secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 300 } }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    No links added yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column: PDF & Visibility */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            {/* PDF Uploads */}
            <Card elevation={0} sx={{ ...caseManagerInsetSurfaceSx, borderRadius: '22px' }}>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <DescriptionIcon sx={{ mr: 1, color: '#e67e22' }} /> PDF Handouts
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <input
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    id="learning-upload"
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="learning-upload">
                    <Button
                      component="span"
                      startIcon={uploading ? null : <UploadIcon />}
                      disabled={uploading}
                      fullWidth
                      sx={{ ...publicSecondaryButtonSx, borderStyle: 'dashed', height: 60 }}
                    >
                      {uploading ? 'Uploading...' : 'Click to Upload PDF Files'}
                    </Button>
                  </label>
                  {uploadError && (
                    <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>
                  )}
                </Box>

                {learning.attachments.length > 0 ? (
                  <List dense sx={{ bgcolor: 'white', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                    {learning.attachments.map((file, index) => (
                      <React.Fragment key={file.id || file.filename}>
                        {index > 0 && <Divider component="li" />}
                        <ListItem
                          secondaryAction={
                            <IconButton edge="end" size="small" onClick={() => handleRemoveAttachment(file.id || file.filename)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <DescriptionIcon color="action" sx={{ mr: 2 }} />
                          <ListItemText
                            primary={file.originalName || file.filename}
                            secondary={
                              <a
                                href={attachmentUrl(file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                              >
                                View / Download
                              </a>
                            }
                            primaryTypographyProps={{ noWrap: true }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    No PDF files uploaded.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {showVisibilityControls ? (
              <Card elevation={0} sx={{ ...caseManagerInsetSurfaceSx, borderRadius: '22px' }}>
                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderBottom: `1px solid ${theme.palette.primary.light}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', color: '#1565c0', fontFamily: publicBodyFontFamily }}>
                    <VisibilityIcon sx={{ mr: 1 }} /> Visibility & Scheduling
                  </Typography>
                </Box>
                <CardContent sx={{ p: 0 }}>
                  <CaseVisibilityPanel
                    value={visibility}
                    onChange={onVisibilityChange}
                    groups={groups}
                    provenance={provenance}
                  />
                </CardContent>
              </Card>
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </CaseEditorStepFrame>
  );
};

export default LearningStep;
